'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'

import { useActiveAccountsScope } from '@/service/account.hook'
import { useWebSocketContext } from '@/service/ws/websocket-context'

import {
  registerRecoveryRequester,
  registerSnapshotGetter,
  registerSnapshotLogger,
} from './recovery-bridge'
import { createRecoveryController } from './recovery-controller'
import { beginResync } from './resync-acks'
import type { ResyncDomain } from './types'

const ACCOUNT_DOMAINS: readonly ResyncDomain[] = ['balances', 'positions', 'orders', 'trades']
const NO_DOMAINS: readonly ResyncDomain[] = []

/**
 * Wires the recovery controller into React.
 *
 * Replaces useVisibilityReconnect: same 1-hour-absence resync, but driven by
 * a controller that also reacts to freeze/resume/pageshow/online and scores the
 * runtime before deciding what to do.
 *
 * Mount once, globally — <AccountDataManager /> owns it.
 */
export function useRuntimeRecovery() {
  const { getHealth, restart } = useWebSocketContext()
  const queryClient = useQueryClient()
  const { hasAccounts } = useActiveAccountsScope()

  // With no active accounts the account streams never send an initial dump, so
  // waiting on their acks would time out every time. Read through a ref so the
  // controller effect doesn't re-run (and re-register listeners) whenever the
  // account scope resolves.
  const hasAccountsRef = useRef(hasAccounts)
  hasAccountsRef.current = hasAccounts

  const getExpectedResyncDomains = useCallback(
    () => (hasAccountsRef.current ? ACCOUNT_DOMAINS : NO_DOMAINS),
    []
  )

  useEffect(() => {
    const controller = createRecoveryController({
      getHealth,
      restart,
      beginResync,
      queryClient,
      getExpectedResyncDomains,
    })

    // Lets code outside this subtree (useConnectionRecovery, under a different
    // layout) reach the controller, and tells it when nothing is mounted.
    const unregister = registerRecoveryRequester(controller.requestRecovery)
    const unregisterSnapshotLogger = registerSnapshotLogger(controller.logSnapshot)
    const unregisterSnapshotGetter = registerSnapshotGetter(controller.getSnapshot)
    const detach = controller.attach()

    return () => {
      unregister()
      unregisterSnapshotLogger()
      unregisterSnapshotGetter()
      detach()
    }
  }, [getHealth, restart, queryClient, getExpectedResyncDomains])
}
